import {rootState} from '@mintdata-wsp/common/state/RootState';
import {SelectionInteractionMode} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {CompassDir} from '@mintdata/mint-kit/api/models/CompassDir';
import {bind} from 'bind-decorator';
import throttle from 'lodash/throttle';
import {action} from 'mobx';

export interface Guideline
{
    left:number;
    top:number;
    width:number;
    height:number;
    snapTo:GuidelineSnapTo;
    deltaWhenSnapped:number;
}

export enum GuidelineSnapTo
{
    LEFT,
    TOP,
    RIGHT,
    BOTTOM,
    VERTICAL,
    HORIZONTAL,
}

export class SurfaceGuidelinesController
{
    private _siblingClientRectMap:Map<string, ClientRect[]> = new Map();
    private _siblingAbsoluteClientRectMap:Map<string, ClientRect[]> = new Map();

    // private static SNAP_THRESHOLD = 5;
    private static get SNAP_THRESHOLD()
    {
        const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;

        return zoom < 1.25 ? 5 : 1;
    };

    public getSnapBounds(interactionMode:SelectionInteractionMode, direction:CompassDir):Bounds | null
    {
        const {surfaceArea, surfaceSelection, surfaceGuidelines} = controllers.ui;

        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        if( !surfaceArea.areBoxesResolved(selectedBoxes) )
            return null;

        const parent = selectedBoxes[0].parent;
        const boxes = parent ? parent.children : surfaceArea.getCurrentLayerPage().artboard.boxes;

        if( !surfaceArea.areBoxesResolved(boxes) )
            return null;

        let siblingRects = this._siblingClientRectMap.get(selectedBoxes[0].id);

        if( !siblingRects )
        {
            siblingRects = boxes
                .filter(box => box.componentDesc.isSelfDirected && selectedBoxes.indexOf(box) === -1)
                .map(box => surfaceArea.getBoxesBounds([box]).toClientRect());

            this._siblingClientRectMap.set(selectedBoxes[0].id, siblingRects);
        }

        const selectionBounds = surfaceSelection.getSelectionBounds();

        if( interactionMode === SelectionInteractionMode.Moving )
            return surfaceGuidelines.getSnapBoundsForMoving(selectionBounds.toClientRect(), siblingRects, direction);

        if( interactionMode === SelectionInteractionMode.Resizing )
            return surfaceGuidelines.getSnapBoundsForResizing(selectionBounds.toClientRect(), siblingRects, direction);

        return null;
    }

    public getSnapBoundsForMoving(target:ClientRect, siblings:ClientRect[] = [], direction:CompassDir):Bounds
    {
        const guidelines = this.computeGuidelines(target, siblings, SelectionInteractionMode.Moving, direction);

        const leftGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.LEFT);
        const topGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.TOP);
        const rightGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.RIGHT);
        const bottomGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.BOTTOM);
        const verticalCenterGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.VERTICAL);
        const horizontalCenterGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.HORIZONTAL);

        const snapBounds = Bounds.from(target.left, target.top, target.width, target.height);

        if( leftGuidelines[0] )
        {
            snapBounds.x = leftGuidelines[0].left;
        }
        else if( rightGuidelines[0] )
        {
            snapBounds.x = rightGuidelines[0].left - target.width;
        }
        else if( verticalCenterGuidelines[0] )
        {
            snapBounds.x = verticalCenterGuidelines[0].left - Math.floor(target.width / 2);
        }

        if( topGuidelines[0] )
        {
            snapBounds.y = topGuidelines[0].top;
        }
        else if( bottomGuidelines[0] )
        {
            snapBounds.y = bottomGuidelines[0].top - target.height;
        }
        else if( horizontalCenterGuidelines[0] )
        {
            snapBounds.y = horizontalCenterGuidelines[0].top - Math.floor(target.height / 2);
        }

        return snapBounds;
    }

    public getSnapBoundsForResizing(target:ClientRect, siblings:ClientRect[] = [], direction:CompassDir):Bounds
    {
        const guidelines = this.computeGuidelines(target, siblings, SelectionInteractionMode.Resizing, direction);

        const leftGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.LEFT);
        const topGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.TOP);
        const rightGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.RIGHT);
        const bottomGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.BOTTOM);
        const verticalCenterGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.VERTICAL);
        const horizontalCenterGuidelines = guidelines.filter(guideline => guideline.snapTo === GuidelineSnapTo.HORIZONTAL);

        const snapBounds = Bounds.from(target.left, target.top, target.width, target.height);

        if( leftGuidelines[0] )
        {
            snapBounds.x = leftGuidelines[0].left;
            snapBounds.width = target.right - snapBounds.x;
        }
        else if( rightGuidelines[0] )
        {
            snapBounds.width = rightGuidelines[0].left - snapBounds.x;
        }
        else if( verticalCenterGuidelines[0] )
        {
            // left
            if( [CompassDir.West, CompassDir.NorthWest, CompassDir.SouthWest].indexOf(direction) !== -1 )
            {
                snapBounds.x = verticalCenterGuidelines[0].left - (target.right - verticalCenterGuidelines[0].left);
                snapBounds.width = (target.right - verticalCenterGuidelines[0].left) * 2;
            }
            // right
            else if( [CompassDir.East, CompassDir.NorthEast, CompassDir.SouthEast].indexOf(direction) !== -1 )
            {
                snapBounds.width = (verticalCenterGuidelines[0].left - target.left) * 2;
            }
        }

        if( topGuidelines[0] )
        {
            snapBounds.y = topGuidelines[0].top;
            snapBounds.height = target.bottom - snapBounds.y;
        }
        else if( bottomGuidelines[0] )
        {
            snapBounds.height = bottomGuidelines[0].top - snapBounds.y;
        }
        else if( horizontalCenterGuidelines[0] )
        {
            // up
            if( [CompassDir.North, CompassDir.NorthWest, CompassDir.NorthEast].indexOf(direction) !== -1 )
            {
                snapBounds.y = horizontalCenterGuidelines[0].top - (target.bottom - horizontalCenterGuidelines[0].top);
                snapBounds.height = (target.bottom - horizontalCenterGuidelines[0].top) * 2;
            }
            // down
            else if( [CompassDir.South, CompassDir.SouthWest, CompassDir.SouthEast].indexOf(direction) !== -1 )
            {
                snapBounds.height = (horizontalCenterGuidelines[0].top - target.top) * 2;
            }
        }

        return snapBounds;
    }

    public readonly throttledUpdateGuidelines = throttle(this._updateGuidelines, 100);

    @bind
    public stopAndClear()
    {
        this.throttledUpdateGuidelines.cancel();

        const {guidelines} = rootState.ui.visualDesigner.surfaceEditor.area;

        guidelines.clear();

        this._siblingClientRectMap.clear();
        this._siblingAbsoluteClientRectMap.clear();
    }

    @action.bound
    private async _updateGuidelines()
    {
        const {
            surfaceSelection,
            surfaceArea,
            surfaceGuidelines,
            surfaceBoxReparenting
        } = controllers.ui;

        const {guidelines} = rootState.ui.visualDesigner.surfaceEditor.area;

        const selectionMode = surfaceSelection.getSelectionInteractionMode();
        const selectionDirection = surfaceSelection.getSelectionInteractionDirection();

        if( surfaceBoxReparenting.isBoxReparentingEnabled() )
            return;

        if( !surfaceSelection.isSelectionMoving(selectionMode) &&
            !surfaceSelection.isSelectionResizing(selectionMode)
        )
            return;

        const selectedBoxes = surfaceSelection.getSelectedBoxes();

        if( !selectedBoxes.length ||
            !surfaceArea.areBoxesResolved(selectedBoxes) ||
            !surfaceArea.areBoxesSiblings(selectedBoxes)
        )
            return;

        const selectedBoxesBounds = surfaceArea.getBoxesBounds(selectedBoxes, true);
        const selectedBoxesRect = selectedBoxesBounds.toClientRect();

        const parent = selectedBoxes[0].parent;
        const boxes = parent
            ? parent.children
                .filter(box => selectedBoxes.indexOf(box) === -1)
                .filter((box) => box.hostId === selectedBoxes[0].hostId)
            : selectedBoxes[0].page.artboard.boxes;

        if( !surfaceArea.areBoxesResolved(boxes) )
            return;

        guidelines.clear();

        let siblingRects = this._siblingAbsoluteClientRectMap.get(selectedBoxes[0].id);

        if( !siblingRects )
        {
            siblingRects = boxes
                .filter(box => box.componentDesc.isSelfDirected && selectedBoxes.map(box => box.id)
                    .indexOf(box.id) === -1)
                .map(box => surfaceArea.getBoxesBounds([box], true).toClientRect());

            this._siblingAbsoluteClientRectMap.set(selectedBoxes[0].id, siblingRects);
        }

        guidelines.replace(surfaceGuidelines.computeGuidelines(
            selectedBoxesRect,
            siblingRects,
            selectionMode,
            selectionDirection
        ));
    }

    public computeGuidelines(target:ClientRect,
                             siblings:ClientRect[] = [],
                             interactionMode:SelectionInteractionMode,
                             direction:CompassDir):Guideline[]
    {
        if( !target || !siblings.length ) return [];

        let leftGuidelines:Guideline[] = [];
        let topGuidelines:Guideline[] = [];
        let rightGuidelines:Guideline[] = [];
        let bottomGuidelines:Guideline[] = [];
        let verticalCenterGuidelines:Guideline[] = [];
        let horizontalCenterGuidelines:Guideline[] = [];

        siblings.forEach((sibling) =>
        {
            this._populateLeftGuidelines(target, sibling, interactionMode, direction, leftGuidelines);
            this._populateTopGuidelines(target, sibling, interactionMode, direction, topGuidelines);
            this._populateRightGuidelines(target, sibling, interactionMode, direction, rightGuidelines);
            this._populateBottomGuidelines(target, sibling, interactionMode, direction, bottomGuidelines);
            this._populateVerticalCenterGuidelines(target, sibling, interactionMode, direction, verticalCenterGuidelines);
            this._populateHorizontalCenterGuidelines(target, sibling, interactionMode, direction, horizontalCenterGuidelines);
        });

        const leftMinDelta = Math.min(...leftGuidelines.map(({deltaWhenSnapped}) => deltaWhenSnapped));
        leftGuidelines = leftGuidelines.filter(({deltaWhenSnapped}) => deltaWhenSnapped === leftMinDelta);

        const topMinDelta = Math.min(...topGuidelines.map(({deltaWhenSnapped}) => deltaWhenSnapped));
        topGuidelines = topGuidelines.filter(({deltaWhenSnapped}) => deltaWhenSnapped === topMinDelta);

        const rightMinDelta = Math.min(...rightGuidelines.map(({deltaWhenSnapped}) => deltaWhenSnapped));
        rightGuidelines = rightGuidelines.filter(({deltaWhenSnapped}) => deltaWhenSnapped === rightMinDelta);

        const bottomMinDelta = Math.min(...bottomGuidelines.map(({deltaWhenSnapped}) => deltaWhenSnapped));
        bottomGuidelines = bottomGuidelines.filter(({deltaWhenSnapped}) => deltaWhenSnapped === bottomMinDelta);

        const verticalCenterMinDelta = Math.min(...verticalCenterGuidelines.map(({deltaWhenSnapped}) => deltaWhenSnapped));
        verticalCenterGuidelines = verticalCenterGuidelines.filter(({deltaWhenSnapped}) => deltaWhenSnapped === verticalCenterMinDelta);

        const horizontalCenterMinDelta = Math.min(...horizontalCenterGuidelines.map(({deltaWhenSnapped}) => deltaWhenSnapped));
        horizontalCenterGuidelines = horizontalCenterGuidelines.filter(({deltaWhenSnapped}) => deltaWhenSnapped === horizontalCenterMinDelta);

        leftGuidelines = this._mergeVerticalCenterGuidelines(leftGuidelines);
        topGuidelines = this._mergeHorizontalCenterGuidelines(topGuidelines);
        rightGuidelines = this._mergeVerticalCenterGuidelines(rightGuidelines);
        bottomGuidelines = this._mergeHorizontalCenterGuidelines(bottomGuidelines);
        verticalCenterGuidelines = this._mergeVerticalCenterGuidelines(verticalCenterGuidelines);
        horizontalCenterGuidelines = this._mergeHorizontalCenterGuidelines(horizontalCenterGuidelines);

        if( leftGuidelines.length && verticalCenterGuidelines.length )
        {
            if( leftGuidelines[0].deltaWhenSnapped <= verticalCenterGuidelines[0].deltaWhenSnapped )
            {
                verticalCenterGuidelines = [];
            }
            else
            {
                leftGuidelines = [];
            }
        }

        if( rightGuidelines.length && verticalCenterGuidelines.length )
        {
            if( rightGuidelines[0].deltaWhenSnapped <= verticalCenterGuidelines[0].deltaWhenSnapped )
            {
                verticalCenterGuidelines = [];
            }
            else
            {
                rightGuidelines = [];
            }
        }

        if( leftGuidelines.length && rightGuidelines.length )
        {
            if( leftGuidelines[0].deltaWhenSnapped <= rightGuidelines[0].deltaWhenSnapped )
            {
                rightGuidelines = [];
            }
            else
            {
                leftGuidelines = [];
            }
        }

        if( topGuidelines.length && horizontalCenterGuidelines.length )
        {
            if( topGuidelines[0].deltaWhenSnapped <= horizontalCenterGuidelines[0].deltaWhenSnapped )
            {
                horizontalCenterGuidelines = [];
            }
            else
            {
                topGuidelines = [];
            }
        }

        if( bottomGuidelines.length && horizontalCenterGuidelines.length )
        {
            if( bottomGuidelines[0].deltaWhenSnapped <= horizontalCenterGuidelines[0].deltaWhenSnapped )
            {
                horizontalCenterGuidelines = [];
            }
            else
            {
                bottomGuidelines = [];
            }
        }

        if( topGuidelines.length && bottomGuidelines.length )
        {
            if( topGuidelines[0].deltaWhenSnapped <= bottomGuidelines[0].deltaWhenSnapped )
            {
                bottomGuidelines = [];
            }
            else
            {
                topGuidelines = [];
            }
        }

        return [
            ...leftGuidelines,
            ...topGuidelines,
            ...rightGuidelines,
            ...bottomGuidelines,
            ...verticalCenterGuidelines,
            ...horizontalCenterGuidelines,
        ];
    }

    private _populateLeftGuidelines(target:ClientRect,
                                    sibling:ClientRect,
                                    interactionMode:SelectionInteractionMode,
                                    direction:CompassDir,
                                    leftGuidelines:Guideline[]):void
    {
        const {surfaceSelection} = controllers.ui;

        if( !(surfaceSelection.isSelectionMoving(interactionMode) ||
            (
                surfaceSelection.isSelectionResizing(interactionMode) &&
                [CompassDir.NorthWest, CompassDir.West, CompassDir.SouthWest].indexOf(direction) !== -1
            )) )
        {
            return;
        }

        const {left, top, bottom} = target;

        const {
            left: siblingLeft,
            top: siblingTop,
            right: siblingRight,
            bottom: siblingBottom,
            width: siblingWidth,
        } = sibling;

        const siblingCenterX = siblingLeft + Math.floor(siblingWidth / 2);

        let guideline:Partial<Guideline> = null;

        const snapThreshold = interactionMode === SelectionInteractionMode.StartResizing
            ? 0
            : SurfaceGuidelinesController.SNAP_THRESHOLD;

        if( Math.abs(left - siblingLeft) <= snapThreshold )
        {
            guideline = {
                left: siblingLeft,
                deltaWhenSnapped: Math.abs(left - siblingLeft),
            };
        }

        if( Math.abs(left - siblingRight) <= snapThreshold )
        {
            guideline = {
                left: siblingRight,
                deltaWhenSnapped: Math.abs(left - siblingRight),
            };
        }

        if( Math.abs(left - siblingCenterX) <= snapThreshold )
        {
            guideline = {
                left: siblingCenterX,
                deltaWhenSnapped: Math.abs(left - siblingCenterX),
            };
        }

        if( guideline )
        {
            leftGuidelines.push({
                top: (siblingTop < top) ? siblingTop : top,
                width: 1,
                height: (siblingTop < top) ? Math.max(bottom, siblingBottom) - siblingTop : siblingBottom - Math.min(top, siblingTop),
                snapTo: GuidelineSnapTo.LEFT,
                ...guideline,
            } as Guideline);
        }
    }

    private _populateTopGuidelines(target:ClientRect,
                                   sibling:ClientRect,
                                   interactionMode:SelectionInteractionMode,
                                   direction:CompassDir,
                                   topGuidelines:Guideline[]):void
    {
        const {surfaceSelection} = controllers.ui;

        if( !(surfaceSelection.isSelectionMoving(interactionMode) ||
            (
                surfaceSelection.isSelectionResizing(interactionMode) &&
                [CompassDir.North, CompassDir.NorthEast, CompassDir.NorthWest].indexOf(direction) !== -1
            )) )
        {
            return;
        }

        const {left, top, right} = target;

        const {
            left: siblingLeft,
            top: siblingTop,
            right: siblingRight,
            bottom: siblingBottom,
            height: siblingHeight,
        } = sibling;

        const siblingCenterY = siblingTop + Math.floor(siblingHeight / 2);

        let guideline:Partial<Guideline> = null;

        const snapThreshold = interactionMode === SelectionInteractionMode.StartResizing
            ? 0
            : SurfaceGuidelinesController.SNAP_THRESHOLD;

        if( Math.abs(top - siblingTop) <= snapThreshold )
        {
            guideline = {
                top: siblingTop,
                deltaWhenSnapped: Math.abs(top - siblingTop),
            };
        }

        if( Math.abs(top - siblingBottom) <= snapThreshold )
        {
            guideline = {
                top: siblingBottom,
                deltaWhenSnapped: Math.abs(top - siblingBottom),
            };
        }

        if( Math.abs(top - siblingCenterY) <= snapThreshold )
        {
            guideline = {
                top: siblingCenterY,
                deltaWhenSnapped: Math.abs(top - siblingCenterY),
            };
        }

        if( guideline )
        {
            topGuidelines.push({
                left: (siblingLeft < left) ? siblingLeft : left,
                width: (siblingLeft < left) ? Math.max(right, siblingRight) - siblingLeft : siblingRight - Math.min(left, siblingLeft),
                height: 1,
                snapTo: GuidelineSnapTo.TOP,
                ...guideline,
            } as Guideline);
        }
    }

    private _populateRightGuidelines(target:ClientRect,
                                     sibling:ClientRect,
                                     interactionMode:SelectionInteractionMode,
                                     direction:CompassDir,
                                     rightGuidelines:Guideline[]):void
    {
        const {surfaceSelection} = controllers.ui;

        if( !(surfaceSelection.isSelectionMoving(interactionMode) ||
            (
                surfaceSelection.isSelectionResizing(interactionMode) &&
                [CompassDir.East, CompassDir.NorthEast, CompassDir.SouthEast].indexOf(direction) !== -1
            )) )
        {
            return;
        }

        const {top, right, bottom} = target;

        const {
            left: siblingLeft,
            top: siblingTop,
            right: siblingRight,
            bottom: siblingBottom,
            width: siblingWidth,
        } = sibling;

        const siblingCenterX = siblingLeft + Math.floor(siblingWidth / 2);

        let guideline:Partial<Guideline> = null;

        const snapThreshold = interactionMode === SelectionInteractionMode.StartResizing
            ? 0
            : SurfaceGuidelinesController.SNAP_THRESHOLD;

        if( Math.abs(right - siblingRight) <= snapThreshold )
        {
            guideline = {
                left: siblingRight,
                deltaWhenSnapped: Math.abs(right - siblingRight),
            };
        }

        if( Math.abs(right - siblingLeft) <= snapThreshold )
        {
            guideline = {
                left: siblingLeft,
                deltaWhenSnapped: Math.abs(right - siblingLeft),
            };
        }

        if( Math.abs(right - siblingCenterX) <= snapThreshold )
        {
            guideline = {
                left: siblingCenterX,
                deltaWhenSnapped: Math.abs(right - siblingCenterX),
            };
        }

        if( guideline )
        {
            rightGuidelines.push({
                top: (siblingTop < top) ? siblingTop : top,
                width: 1,
                height: (siblingTop < top) ? Math.max(bottom, siblingBottom) - siblingTop : siblingBottom - Math.min(top, siblingTop),
                snapTo: GuidelineSnapTo.RIGHT,
                ...guideline,
            } as Guideline);
        }
    }

    private _populateBottomGuidelines(target:ClientRect,
                                      sibling:ClientRect,
                                      interactionMode:SelectionInteractionMode,
                                      direction:CompassDir,
                                      bottomGuidelines:Guideline[]):void
    {
        const {surfaceSelection} = controllers.ui;

        if( !(surfaceSelection.isSelectionMoving(interactionMode) ||
            (
                surfaceSelection.isSelectionResizing(interactionMode) &&
                [CompassDir.South, CompassDir.SouthEast, CompassDir.SouthWest].indexOf(direction) !== -1
            )) )
        {
            return;
        }

        const {left, right, bottom} = target;

        const {
            left: siblingLeft,
            top: siblingTop,
            right: siblingRight,
            bottom: siblingBottom,
            height: siblingHeight,
        } = sibling;

        const siblingCenterY = siblingTop + Math.floor(siblingHeight / 2);

        let guideline:Partial<Guideline> = null;

        const snapThreshold = interactionMode === SelectionInteractionMode.StartResizing
            ? 0
            : SurfaceGuidelinesController.SNAP_THRESHOLD;

        if( Math.abs(bottom - siblingBottom) <= snapThreshold )
        {
            guideline = {
                top: siblingBottom,
                deltaWhenSnapped: Math.abs(bottom - siblingBottom),
            };
        }

        if( Math.abs(bottom - siblingTop) <= snapThreshold )
        {
            guideline = {
                top: siblingTop,
                deltaWhenSnapped: Math.abs(bottom - siblingTop),
            };
        }

        if( Math.abs(bottom - siblingCenterY) <= snapThreshold )
        {
            guideline = {
                top: siblingCenterY,
                deltaWhenSnapped: Math.abs(bottom - siblingCenterY),
            };
        }

        if( guideline )
        {
            bottomGuidelines.push({
                left: (siblingLeft < left) ? siblingLeft : left,
                width: (siblingLeft < left) ? Math.max(right, siblingRight) - siblingLeft : siblingRight - Math.min(left, siblingLeft),
                height: 1,
                snapTo: GuidelineSnapTo.BOTTOM,
                ...guideline,
            } as Guideline);
        }
    }

    private _populateVerticalCenterGuidelines(target:ClientRect,
                                              sibling:ClientRect,
                                              interactionMode:SelectionInteractionMode,
                                              direction:CompassDir,
                                              verticalCenterGuidelines:Guideline[]):void
    {
        const {surfaceSelection} = controllers.ui;

        if( !(surfaceSelection.isSelectionMoving(interactionMode) ||
            (
                surfaceSelection.isSelectionResizing(interactionMode) &&
                [CompassDir.North, CompassDir.South].indexOf(direction) === -1
            )) )
        {
            return;
        }

        const {left, top, width, height} = target;

        const centerX = left + Math.floor(width / 2);
        const centerY = top + Math.floor(height / 2);

        const {
            left: siblingLeft,
            top: siblingTop,
            right: siblingRight,
            bottom: siblingBottom,
            width: siblingWidth,
        } = sibling;

        const siblingCenterX = siblingLeft + Math.floor(siblingWidth / 2);

        let guideline:Partial<Guideline> = null;

        const snapThreshold = interactionMode === SelectionInteractionMode.StartResizing
            ? 0
            : SurfaceGuidelinesController.SNAP_THRESHOLD;

        if( Math.abs(centerX - siblingLeft) <= snapThreshold )
        {
            guideline = {
                left: siblingLeft,
                deltaWhenSnapped: Math.abs(centerX - siblingLeft),
            };
        }

        if( Math.abs(centerX - siblingRight) <= snapThreshold )
        {
            guideline = {
                left: siblingRight,
                deltaWhenSnapped: Math.abs(centerX - siblingRight),
            };
        }

        if( Math.abs(centerX - siblingCenterX) <= snapThreshold )
        {
            guideline = {
                left: siblingCenterX,
                deltaWhenSnapped: Math.abs(centerX - siblingCenterX),
            };
        }

        if( guideline )
        {
            verticalCenterGuidelines.push({
                top: (siblingTop < top) ? siblingTop : Math.min(centerY, siblingTop),
                width: 1,
                height: (siblingTop < centerY) ? Math.max(centerY, siblingBottom) - siblingTop : siblingBottom - Math.min(centerY, siblingTop),
                snapTo: GuidelineSnapTo.VERTICAL,
                ...guideline,
            } as Guideline);
        }
    }

    private _populateHorizontalCenterGuidelines(target:ClientRect,
                                                sibling:ClientRect,
                                                interactionMode:SelectionInteractionMode,
                                                direction:CompassDir,
                                                horizontalCenterGuidelines:Guideline[]):void
    {
        const {surfaceSelection} = controllers.ui;

        if( !(surfaceSelection.isSelectionMoving(interactionMode) ||
            (
                surfaceSelection.isSelectionResizing(interactionMode) &&
                [CompassDir.West, CompassDir.East].indexOf(direction) === -1
            )) )
        {
            return;
        }

        const {left, top, width, height} = target;

        const centerX = left + Math.floor(width / 2);
        const centerY = top + Math.floor(height / 2);

        const {
            left: siblingLeft,
            top: siblingTop,
            right: siblingRight,
            bottom: siblingBottom,
            height: siblingHeight
        } = sibling;

        const siblingCenterY = siblingTop + Math.floor(siblingHeight / 2);

        let guideline:Partial<Guideline> = null;

        const snapThreshold = interactionMode === SelectionInteractionMode.StartResizing
            ? 0
            : SurfaceGuidelinesController.SNAP_THRESHOLD;

        if( Math.abs(centerY - siblingTop) <= snapThreshold )
        {
            guideline = {
                top: siblingTop,
                deltaWhenSnapped: Math.abs(centerY - siblingTop),
            };
        }

        if( Math.abs(centerY - siblingBottom) <= snapThreshold )
        {
            guideline = {
                top: siblingBottom,
                deltaWhenSnapped: Math.abs(centerY - siblingBottom),
            };
        }

        if( Math.abs(centerY - siblingCenterY) <= snapThreshold )
        {
            guideline = {
                top: siblingCenterY,
                deltaWhenSnapped: Math.abs(centerY - siblingCenterY),
            };
        }

        if( guideline )
        {
            horizontalCenterGuidelines.push({
                left: (siblingLeft < left) ? siblingLeft : Math.min(centerX, siblingLeft),
                width: (siblingLeft < centerX) ? Math.max(centerX, siblingRight) - siblingLeft : siblingRight - Math.min(centerX, siblingLeft),
                height: 1,
                snapTo: GuidelineSnapTo.HORIZONTAL,
                ...guideline,
            } as Guideline);
        }
    }

    private _mergeHorizontalCenterGuidelines(topGuidelines:Guideline[]):Guideline[]
    {
        if( !topGuidelines.length )
            return [];

        const base = topGuidelines.reduce((p, v) => v.left < p.left ? v : p);
        const farthest = topGuidelines.reduce((p, v) => v.left + v.width > p.left + p.width ? v : p);
        const width = farthest.left + farthest.width - base.left;

        return [{...base, width}];
    }

    private _mergeVerticalCenterGuidelines(rightGuidelines:Guideline[]):Guideline[]
    {
        if( !rightGuidelines.length )
            return [];

        const base = rightGuidelines.reduce((p, v) => v.top < p.top ? v : p);
        const farthest = rightGuidelines.reduce((p, v) => v.top + v.height > p.top + p.height ? v : p);
        const height = farthest.top + farthest.height - base.top;

        return [{...base, height}];
    }
}
